import {
  FunctionsOutlined,
  OutlinedFlagOutlined,
  StarBorderOutlined,
  ThumbDownAltOutlined,
  TrendingDown,
  TrendingFlat,
  TrendingUp,
} from "@mui/icons-material"
import React, { useMemo } from "react"
import { useSelector } from "react-redux"

const SentimentAnalysisCard = ({
  title,
  labels = [],
  data = [],
  color = "#2990DF",
  isLoading = false,
}) => {
  const mode = useSelector(s => s.Layout?.layoutMode)
  const isDark = String(mode).toLowerCase().includes("dark")

  const chartWidth = 600
  const chartHeight = 270

  const safeData = Array.isArray(data) ? data.map(Number) : []
  const safeLabels = Array.isArray(labels) ? labels : []

  const stats = useMemo(() => {
    const n = safeData.length
    if (!n) {
      return {
        avg: null,
        last: null,
        prev: null,
        delta: null,
        pct: null,
        max: null,
        min: null,
        maxLabel: "-",
        minLabel: "-",
      }
    }
    let sum = 0
    let max = -Infinity
    let min = Infinity
    let iMax = 0
    let iMin = 0
    safeData.forEach((v, i) => {
      sum += v
      if (v > max) {
        max = v
        iMax = i
      }
      if (v < min) {
        min = v
        iMin = i
      }
    })
    const avg = sum / n
    const last = safeData[n - 1]
    const prev = n >= 2 ? safeData[n - 2] : null
    const delta = prev == null ? null : last - prev
    const pct = prev && prev !== 0 ? (delta / prev) * 100 : null

    return {
      avg,
      last,
      prev,
      delta,
      pct,
      max,
      min,
      maxLabel: safeLabels[iMax] ?? "-",
      minLabel: safeLabels[iMin] ?? "-",
    }
  }, [safeData, safeLabels])

  const { maxValue, yTicks } = useMemo(() => {
    const max = Math.max(1, ...safeData, 0)
    const m1 = max
    const m2 = Math.floor((max * 3) / 4)
    const m3 = Math.floor(max / 2)
    const m4 = Math.floor(max / 4)
    return { maxValue: m1, yTicks: [m1, m2, m3, m4, 0] }
  }, [safeData])

  const pathD = useMemo(() => {
    return createSmoothPath(
      safeData,
      safeLabels.length,
      chartHeight,
      maxValue,
      chartWidth,
    )
  }, [safeData, safeLabels, maxValue])

  const ui = {
    surface: isDark ? "#252F40" : "#FFFFFF",
    cardShadow: isDark
      ? "0 5px 14px rgba(0,0,0,0.5)"
      : "0 5px 14px rgba(0,0,0,0.1)",
    heading: isDark ? "#FFFFFF" : "#111827",
    muted: isDark ? "#9CA3AF" : "#6B7280",
    axis: isDark ? "#374151" : "#E5E7EB",
    xLabel: isDark ? "#9CA3AF" : "#4B5563",
    good: "#58DB83",
    bad: "#EF4444",
    accent: "#2990DF",
  }

  const hasSinglePoint = safeData.length === 1

  const Kpi = ({ label, value, sub, tone, icon }) => (
    <div
    className="br"
      style={{
        display: "flex",
        justifyContent: "center",
        gap: "12px",
        alignItems: "center",
        padding: "20px",
        boxShadow:
          mode === "dark"
            ? "0px 5px 14px 0px rgba(0,0,0,0.5)"
            : "0px 5px 14px 0px #E5E7EB",
      }}
    >
      {icon}
      <div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div
            style={{
              fontSize: "1.875rem",
              fontWeight: "bold",
              color: mode === "dark" ? "#fff" : "#1F2937",
            }}
          >
            {value}
          </div>

          {label === "Latest vs Previous" && (
            <div>
              {value == 0 ? (
                <TrendingFlat sx={{ color: "yellow" }} />
              ) : value > 0 ? (
                <TrendingUp sx={{ color: "green" }} />
              ) : (
                <TrendingDown sx={{ color: "red" }} />
              )}
            </div>
          )}
        </div>
        <div
          style={{
            fontSize: "0.875rem",
            color: mode === "dark" ? "#94A3B8" : "#05040969",
          }}
        >
          {label}
        </div>
      </div>
    </div>
  )
  const fmt = v => (v == null || Number.isNaN(v) ? "—" : Number(v).toFixed(2))
  const fmtPct = v =>
    v == null || Number.isNaN(v) ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`
  const fmtDelta = v =>
    v == null || Number.isNaN(v) ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(2)}`

  const deltaTone =
    stats.delta == null ? undefined : stats.delta >= 0 ? ui.good : ui.bad

  return (
    <div
    className="br"
      style={{
        width: "100%",
        height: "100%",
        background: ui.surface,
        padding: 22,
        boxShadow: ui.cardShadow,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "32px",
          marginTop: 8,
        }}
      >
        <h2
          style={{
            fontSize: "1.25rem",
            fontWeight: 600,
            color: mode === "dark" ? "white" : "#1F2937",
          }}
        >
          {title || "Sentiment"}
        </h2>
      </div>
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: "32px",
          justifyContent: "space-around",
        }}
      >
        <Kpi
          label="Latest vs Previous"
          value={fmtDelta(stats.delta)}
          sub={`Change ${fmtPct(stats.pct)}`}
          tone={deltaTone}
          icon={
            <OutlinedFlagOutlined
              sx={{
                color: mode === "dark" ? "#ffffff" : "#2990DF",
                fontSize: 32,
              }}
            />
          }
        />
        <Kpi
          label="Best Campaign"
          value={fmt(stats.max)}
          sub={stats.maxLabel}
          tone={ui.good}
          icon={
            <StarBorderOutlined
              sx={{
                color: mode === "dark" ? "#ffffff" : "#2990DF",
                fontSize: 32,
              }}
            />
          }
        />

        <Kpi
          label="Average"
          value={fmt(stats.avg)}
          sub="Across periods"
          tone={ui.accent}
          icon={
            <FunctionsOutlined
              sx={{
                color: mode === "dark" ? "#ffffff" : "#2990DF",
                fontSize: 32,
              }}
            />
          }
        />

        <Kpi
          label="Worst Campaign"
          value={fmt(stats.min)}
          sub={stats.minLabel}
          tone={ui.bad}
          icon={
            <ThumbDownAltOutlined
              sx={{
                color: mode === "dark" ? "#ffffff" : "#2990DF",
                fontSize: 32,
              }}
            />
          }
        />
      </div>
      <div style={{ position: "relative", height: chartHeight }}>
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 32,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            fontSize: "0.75rem",
            color: ui.muted,
            width: 32,
          }}
        >
          {yTicks.map((v, i) => (
            <span key={i}>{v}</span>
          ))}
        </div>
        <div
          style={{ marginLeft: 40, position: "relative", height: chartHeight }}
        >
          {isLoading ? (
            <div
              style={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: ui.muted,
              }}
            >
              Loading…
            </div>
          ) : (
            <>
              <svg
                width="100%"
                height="100%"
                viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                preserveAspectRatio="none"
              >
                <defs>
                  <pattern
                    id="grid"
                    width="100%"
                    height="25%"
                    patternUnits="userSpaceOnUse"
                  >
                    <line
                      x1="0"
                      y1="0"
                      x2="100%"
                      y2="0"
                      stroke={ui.axis}
                      strokeWidth="1"
                    />
                  </pattern>
                </defs>

                <rect width="100%" height="100%" fill="url(#grid)" />

                {safeLabels.length > 0 && safeData.length > 0 && (
                  <path
                    d={pathD}
                    fill="none"
                    stroke="#2990DF"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                )}

                {hasSinglePoint &&
                  (() => {
                    const padding = 20
                    const x = padding
                    const usableH = chartHeight - 60
                    const y =
                      chartHeight -
                      30 -
                      (safeData[0] / Math.max(1, maxValue)) * usableH
                    return <circle cx={x} cy={y} r="5" fill={color} />
                  })()}
              </svg>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 0,
                  padding: "0 8px",
                  fontSize: "0.75rem",
                  color: ui.xLabel,
                }}
              >
                {safeLabels.map((lbl, i) => (
                  <span
                    key={i}
                    style={{
                      width: `${100 / Math.max(1, safeLabels.length)}%`,
                      textAlign: "center",
                    }}
                  >
                    {lbl}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default SentimentAnalysisCard

function createSmoothPath(data, pointCount, height, maxValue, width) {
  if (!data || !data.length || pointCount === 0) return ""
  const padding = 20
  const stepX = pointCount > 1 ? (width - padding * 2) / (pointCount - 1) : 0

  const usableH = height - 60
  const points = data.map((value, index) => ({
    x: padding + index * stepX,
    y: height - 30 - (Number(value || 0) / Math.max(1, maxValue)) * usableH,
  }))

  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`

  let path = `M ${points[0].x} ${points[0].y}`
  for (let i = 0; i < points.length - 1; i++) {
    const current = points[i]
    const next = points[i + 1]
    const cpd = (next.x - current.x) * 0.5
    const cp1x = current.x + cpd,
      cp1y = current.y
    const cp2x = next.x - cpd,
      cp2y = next.y
    path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${next.x} ${next.y}`
  }
  return path
}
